import { IAddress, IAddressDistrict, IDistrict } from "../../../classes/interfaces";

/**
 * Endereços salvos do cliente (localStorage "__addss").
 *
 * A chave é compartilhada por TODAS as lojas MenuChef do domínio. O formato
 * antigo guardava um `districtId` único por endereço — o id do bairro da loja em
 * que o endereço foi salvo, que em outra loja não significa nada (ou é o bairro
 * da outra loja) e gerava taxa 0 ou a taxa errada. O formato atual guarda o
 * bairro por loja (`districtByStore[storeId]`) e só o nome em `district`.
 */
export const SAVED_ADDRESSES_KEY = "__addss";

/** O que os helpers precisam da loja: o id e os bairros ativos dela. */
export interface SavedAddressStore {
  id: string;
  districts?: Pick<IDistrict, "id" | "name">[] | null;
}

/** Campos do formulário de endereço usados para gravar o endereço salvo. */
export interface SavedAddressForm {
  address: string;
  number: string;
  district: string;
  /** Bairro desta loja escolhido no formulário (null = loja sem bairro). */
  districtId?: number | null;
  cityId: number;
  city: string;
  zipCode: string;
  complement: string;
  reference: string;
}

type StorageLike = Pick<Storage, "getItem" | "setItem">;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asText = (value: unknown): string =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : "";

const asId = (value: unknown): number | null => {
  const id =
    typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof id === "number" && Number.isInteger(id) && id > 0 ? id : null;
};

/** Chave nova, que não repete nenhuma das existentes (nem depois de apagar). */
export function generateAddressKey(existingKeys: Iterable<string> = []): string {
  const used = new Set(existingKeys);
  let key: string;
  do {
    key = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  } while (used.has(key));
  return key;
}

function normalizeDistrictByStore(value: unknown): {
  [storeId: string]: IAddressDistrict;
} {
  const result: { [storeId: string]: IAddressDistrict } = {};
  if (!isRecord(value)) return result;
  Object.keys(value).forEach((storeId) => {
    const entry = value[storeId];
    if (!storeId || !isRecord(entry)) return;
    const id = asId(entry.id);
    if (id === null) return;
    result[storeId] = { id, name: asText(entry.name) };
  });
  return result;
}

/**
 * Converte o conteúdo de "__addss" (já parseado, qualquer coisa) para o formato
 * atual. Idempotente: rodar de novo no resultado devolve o mesmo resultado.
 *
 * - Formato antigo `{ districtId, district, ... }`: `district` vira o nome
 *   (dica). O `districtId` antigo é descartado — ele só é aproveitado como o
 *   bairro DESTA loja se estiver entre os bairros ativos dela (verificado aqui,
 *   então é seguro). Em qualquer outra loja o cliente escolhe o bairro uma vez.
 * - Lixo (não é lista, item que não é objeto, item sem rua) é ignorado.
 * - Chave ausente ou repetida (o cadastro antigo usava `length + 1`, que repete
 *   depois de apagar um endereço) ganha uma chave nova; as demais ficam.
 */
export function migrateSavedAddresses(
  raw: unknown,
  store: SavedAddressStore,
): IAddress[] {
  if (!Array.isArray(raw)) return [];

  const storeDistricts = new Map<number, string>();
  (store.districts || []).forEach((district) => {
    storeDistricts.set(district.id, district.name);
  });

  const usedKeys = new Set<string>();
  const result: IAddress[] = [];

  raw.forEach((item) => {
    if (!isRecord(item)) return;
    const address = asText(item.address);
    if (address.trim() === "") return;

    const district = asText(item.district);
    const districtByStore = normalizeDistrictByStore(item.districtByStore);

    if ("districtId" in item && store.id && !districtByStore[store.id]) {
      const legacyId = asId(item.districtId);
      if (legacyId !== null && storeDistricts.has(legacyId)) {
        districtByStore[store.id] = {
          id: legacyId,
          name: storeDistricts.get(legacyId) || district,
        };
      }
    }

    let key = asText(item.key);
    if (key === "" || usedKeys.has(key)) {
      key = generateAddressKey(usedKeys);
    }
    usedKeys.add(key);

    const migrated: IAddress = {
      key,
      address,
      number: asText(item.number),
      district,
      cityId: asId(item.cityId) ?? 0,
      city: asText(item.city),
      zipCode: asText(item.zipCode),
      complement: asText(item.complement),
      reference: asText(item.reference),
    };
    if (Object.keys(districtByStore).length > 0) {
      migrated.districtByStore = districtByStore;
    }
    result.push(migrated);
  });

  return result;
}

/** Grava os endereços. Falha de storage (cota, modo privado) não derruba a tela. */
export function writeSavedAddresses(
  storage: StorageLike,
  addresses: IAddress[],
): void {
  try {
    storage.setItem(SAVED_ADDRESSES_KEY, JSON.stringify(addresses));
  } catch {
    // Sem onde gravar: os endereços valem só para esta tela.
  }
}

/**
 * Lê "__addss", migra para o formato atual e regrava se algo mudou. Nunca
 * lança: JSON inválido vira lista vazia (e é regravado como `[]`).
 */
export function readSavedAddresses(
  storage: StorageLike,
  store: SavedAddressStore,
): IAddress[] {
  let raw: string | null;
  try {
    raw = storage.getItem(SAVED_ADDRESSES_KEY);
  } catch {
    return [];
  }
  if (raw === null) return [];

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }

  const addresses = migrateSavedAddresses(parsed, store);
  if (JSON.stringify(addresses) !== raw) {
    writeSavedAddresses(storage, addresses);
  }
  return addresses;
}

/** Bairro gravado para esta loja, sem conferir se ainda é válido. */
export function getSavedStoreDistrict(
  address: IAddress,
  storeId: string,
): IAddressDistrict | null {
  return address.districtByStore?.[storeId] ?? null;
}

/**
 * Bairro do endereço nesta loja, só se ele estiver entre os bairros ativos da
 * loja (o nome vem da loja). null = o cliente precisa escolher o bairro aqui.
 */
export function getStoreDistrict(
  address: IAddress,
  store: SavedAddressStore,
): IAddressDistrict | null {
  const saved = getSavedStoreDistrict(address, store.id);
  if (!saved) return null;
  const district = (store.districts || []).find((d) => d.id === saved.id);
  return district ? { id: district.id, name: district.name } : null;
}

const sameLocation = (a: SavedAddressForm | IAddress, b: SavedAddressForm | IAddress) =>
  a.address.trim() === b.address.trim() &&
  a.number.trim() === b.number.trim() &&
  a.cityId === b.cityId &&
  a.zipCode.trim() === b.zipCode.trim();

/**
 * Monta o endereço salvo a partir do formulário.
 *
 * - O bairro escolhido vale só para esta loja: `districtByStore[store.id]`, e o
 *   nome vira o `district` (dica para as outras lojas).
 * - Os bairros das OUTRAS lojas ficam como estavam, a não ser que o cliente
 *   tenha mudado o local (rua, número, cidade ou CEP): aí eles eram de outro
 *   lugar e são descartados — cada loja pede o bairro de novo.
 */
export function buildSavedAddress(
  form: SavedAddressForm,
  storeId: string,
  key: string,
  previous?: IAddress,
): IAddress {
  const districtByStore: { [storeId: string]: IAddressDistrict } =
    previous && sameLocation(previous, form)
      ? { ...(previous.districtByStore || {}) }
      : {};
  if (form.districtId != null) {
    districtByStore[storeId] = { id: form.districtId, name: form.district };
  }

  const saved: IAddress = {
    key,
    address: form.address,
    number: form.number,
    district: form.district,
    cityId: form.cityId,
    city: form.city,
    zipCode: form.zipCode,
    complement: form.complement,
    reference: form.reference,
  };
  if (Object.keys(districtByStore).length > 0) {
    saved.districtByStore = districtByStore;
  }
  return saved;
}
